import React from "react";
import { LucideIcon, AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "../button";
import { useTranslation } from "react-i18next";

interface ErrorPlaceholderProps {
  icon?: LucideIcon;
  title?: string;
  description?: string;
  error?: string;
  className?: string;
  iconClassName?: string;
  onRetry?: () => void;
  showRetryButton?: boolean;
}

export const ErrorPlaceholder: React.FC<ErrorPlaceholderProps> = ({
  icon: Icon,
  title,
  description,
  error,
  className = "",
  iconClassName = "text-red-400",
  onRetry,
  showRetryButton = true,
}) => {
  const { t } = useTranslation();

  const defaultTitle = title || t("ui.something_went_wrong");
  const defaultDescription = description || t("ui.error_loading_data");
  return (
    <div
      className={`flex flex-col items-center justify-center py-12 ${className}`}
    >
      <div className="w-16 h-16 bg-red-50 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-4">
        {Icon ? (
          <Icon className={`w-8 h-8 ${iconClassName}`} />
        ) : (
          <AlertCircle className={`w-8 h-8 ${iconClassName}`} />
        )}
      </div>
      <div className="text-center flex">
        <div className="text-lg font-medium mb-2 text-gray-900 dark:text-gray-100">
          {defaultTitle}
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          {defaultDescription}
        </div>
        {error && (
          <div className="text-xs text-red-500 mb-4 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded">
            {error}
          </div>
        )}
        {showRetryButton && onRetry && (
          <Button
            variant="outline"
            size="sm"
            onClick={onRetry}
            className="flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            {t("ui.try_again")}
          </Button>
        )}
      </div>
    </div>
  );
};
